import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthSession, fetchAuthSession, getCurrentUser, JWT, signIn, type SignInInput, signOut } from 'aws-amplify/auth';
import { Category } from '../@core/data/categories';
import { EnvironmentService } from '../@core/services/environment.service';

export interface CategoryResponse {
  data: Category[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalCount: number;
    totalPages?: number;
  };
}


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private router: Router,
    private readonly environmentService: EnvironmentService,
  ) { }

  // Login using Amplify
  async login({ username, password }: SignInInput): Promise<boolean> {
    const user = await signIn({ username, password });

    /*
    if (user.nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      // Store user temporarily for new password challenge
      sessionStorage.setItem('cognitoUser', JSON.stringify(user));
      this.router.navigate(['/newPasswordRequire']);
      return false;
    } else {

      console.log('Login successful:', user);
      this.router.navigate(['']);
      return true;
    }
  } catch (error: any) {
    console.error('Login error:', error);

    // Handle specific Cognito errors
    switch (error.code) {
      case 'UserNotConfirmedException':
        console.log('User email not confirmed');
        break;
      case 'NotAuthorizedException':
        console.log('Invalid credentials');
        break;
      case 'UserNotFoundException':
        console.log('User not found');
        break;
      default:
        console.log('Login failed:', error.message);
    }
    return false;

    */

    this.router.navigate(['']);
    return true;
  }

  // Handle new password requirement
  /*async changePassword(newPassword: string, confirmPassword: string): Promise<boolean> {
    if (newPassword !== confirmPassword) {
      console.log("Passwords don't match");
      return false;
    }

    try {
      // Get the user from session storage
      const userString = sessionStorage.getItem('cognitoUser');
      if (!userString) {
        console.error('No user found for password change');
        return false;
      }

      const user = JSON.parse(userString);

      // Complete the new password challenge
      const result = await completeNewPassword(user, newPassword, {});

      console.log('Password change successful:', result);
      sessionStorage.removeItem('cognitoUser');
      this.router.navigate(['/login']);
      return true;

    } catch (error) {
      console.error('Password change failed:', error);
      return false;
    }
  }*/

  // Logout using Amplify
  async logOut(): Promise<void> {
    try {
      await signOut();
      this.router.navigate(['auth/login']);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Get current authenticated user
  async getCurrentUser(): Promise<any> {
    try {
      return await getCurrentUser();
    } catch (error) {
      return null;
    }
  }

  // Get current session and tokens
  async getCurrentSession(): Promise<any> {
    const session = await fetchAuthSession();
    if (!session.tokens) {
      return null
    }
    return session;
  }

  // Get access token
  async getAccessToken(): Promise<string> {
    try {
      const session: AuthSession = await fetchAuthSession();

      if (session.tokens !== null) {
        const accessToken: JWT | undefined = session.tokens?.idToken;
        return accessToken ? accessToken.toString() : '';
      }
      return '';
    } catch (err) {
      console.log(err);
      return '';
    }
  }

  // Get ID token
  async getIdToken(): Promise<string | null> {
    try {
      const session = await this.getCurrentSession();
      return session.getIdToken().getJwtToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    return await this.getCurrentSession() !== null;
  }

  // Refresh tokens
  async refreshSession(): Promise<any> {
    try {
      const user = await this.getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh: true });
      return session;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return null;
    }
  }
}
